import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/companyService';
import { stripeService } from '../../services/stripeService';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';

const ToggleRow = ({ label, description, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-border last:border-0">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
        checked ? 'bg-accent' : 'bg-muted'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

const CompanySettings = () => {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const isAdmin = userProfile?.role === 'admin';

  const [companyId, setCompanyId] = useState(null);
  const [company, setCompany] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Subscription state
  const [siteUsage, setSiteUsage] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Editable company info
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoSuccess, setInfoSuccess] = useState(false);
  const [infoError, setInfoError] = useState('');

  const [settings, setSettings] = useState({
    require_photo_evidence: false,
    require_photo_evidence_snag: false,
    require_toolbox_talk_signoff: false,
    enable_incident_reporting: true,
    expiry_alert_days: 30,
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

  useEffect(() => {
    if (!isAdmin) return;
    loadSettings();
  }, [isAdmin]);

  const loadSettings = async () => {
    try {
      setSettingsLoading(true);
      const { data, error } = await companyService?.getUserCompany();
      if (error || !data) return;
      setCompanyId(data?.id);
      setCompany(data);
      setEditCompanyName(data?.company_name || '');
      setEditContactEmail(data?.contact_email || '');
      setSettings({
        require_photo_evidence: data?.require_photo_evidence ?? false,
        require_photo_evidence_snag: data?.require_photo_evidence_snag ?? false,
        require_toolbox_talk_signoff: data?.require_toolbox_talk_signoff ?? false,
        enable_incident_reporting: data?.enable_incident_reporting ?? true,
        expiry_alert_days: data?.expiry_alert_days ?? 30,
      });

      // Load site usage
      const { data: usage } = await companyService?.getActiveSiteUsage(data?.id);
      if (usage) setSiteUsage(usage);
    } catch (err) {
      // silently fail
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleToggle = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
    setSaveError('');
  };

  // Derived validation for the expiry-alert-days number input. Range matches
  // the CHECK constraint on companies.expiry_alert_days (1..365). Empty /
  // non-integer input is also rejected. See JmthfUaq.
  const expiryAlertDaysError = (() => {
    const n = Number(settings?.expiry_alert_days);
    if (settings?.expiry_alert_days === '' || settings?.expiry_alert_days == null) {
      return 'Please enter a value between 1 and 365.';
    }
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      return 'Must be a whole number between 1 and 365.';
    }
    return '';
  })();

  const handleSaveSettings = async () => {
    if (!companyId) return;
    if (expiryAlertDaysError) {
      setSaveError(expiryAlertDaysError);
      return;
    }
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const { error } = await companyService?.updateCompanySettings(companyId, settings);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('[CompanySettings] save settings failed:', err);
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompanyInfo = async () => {
    if (!companyId) return;
    setInfoSaving(true);
    setInfoSuccess(false);
    setInfoError('');
    try {
      const { error } = await supabase
        ?.from('companies')
        ?.update({ company_name: editCompanyName?.trim(), contact_email: editContactEmail?.trim() })
        ?.eq('id', companyId);
      if (error) throw error;
      setCompany(prev => ({ ...prev, company_name: editCompanyName?.trim(), contact_email: editContactEmail?.trim() }));
      setInfoSuccess(true);
      setTimeout(() => setInfoSuccess(false), 3000);
    } catch (err) {
      console.error('[CompanySettings] save company info failed:', err);
      setInfoError('Failed to save company info. Please try again.');
    } finally {
      setInfoSaving(false);
    }
  };

  const handleOpenPortal = async () => {
    if (!companyId) return;
    setPortalLoading(true);
    try {
      const { data, error } = await stripeService?.createPortalSession(companyId);
      if (error) throw error;
      if (data?.url) window.open(data?.url, '_blank');
    } catch (err) {
      // silently fail — portal may not be available in all environments
    } finally {
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== CONFIRM_PHRASE) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const { data: companyData, error: companyError } = await companyService?.getUserCompany();
      if (companyError || !companyData?.id) throw new Error(companyError?.message || 'Could not load company');
      const { error } = await companyService?.deleteCompany(companyData?.id);
      if (error) throw error;
      await signOut?.();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('[CompanySettings] delete account failed:', err);
      setDeleteError('Account deletion failed. Please try again.');
      setDeleting(false);
    }
  };

  const statusBadge = company ? stripeService?.getStatusBadge(company?.subscription_status) : null;
  const activeSites = siteUsage?.active_count ?? 0;
  const maxSites = company?.max_active_sites ?? siteUsage?.max_sites ?? '—';
  const usagePct = siteUsage?.usage_percentage ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-[72px]">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <SubscriptionReadOnlyBanner className="mb-6" />
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-9 h-9 rounded-md border border-border hover:bg-muted transition-colors"
            >
              <Icon name="ArrowLeft" size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('companySettingsPageTitle')}</h1>
              <p className="text-sm text-muted-foreground">{t('companySettingsPageSubtitle')}</p>
            </div>
          </div>

          {/* ── Subscription & Billing ── */}
          {isAdmin && (
            <div className="bg-card rounded-lg shadow-elevation-1 p-6 mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="CreditCard" size={20} className="text-accent" />
                {t('subscriptionAndBilling')}
              </h2>

              {settingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Plan + status row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 border border-border rounded-lg">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{t('currentPlanLabel')}</p>
                      <p className="text-foreground font-semibold text-lg">{company?.plan_tier || '—'}</p>
                    </div>
                    <div className="p-4 border border-border rounded-lg">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{t('billingStatus')}</p>
                      {statusBadge ? (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium border ${statusBadge?.className}`}>
                          {statusBadge?.label}
                        </span>
                      ) : (
                        <p className="text-foreground font-semibold">—</p>
                      )}
                    </div>
                    <div className="p-4 border border-border rounded-lg">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{t('activeSitesLabel2')}</p>
                      <p className="text-foreground font-semibold text-lg">
                        {activeSites} <span className="text-muted-foreground font-normal text-sm">/ {maxSites === null ? '∞' : maxSites}</span>
                      </p>
                      {maxSites !== null && maxSites !== '—' && (
                        <div className="mt-2">
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${usagePct >= 100 ? 'bg-destructive' : usagePct >= 80 ? 'bg-warning' : 'bg-success'}`}
                              style={{ width: `${Math.min(usagePct, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{usagePct}{t('usedPercent')}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manage Subscription button */}
                  <div className="flex items-center justify-between p-4 bg-muted/40 rounded-lg border border-border">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('manageSubscription')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('manageSubscriptionDesc2')}</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleOpenPortal}
                      loading={portalLoading}
                      disabled={portalLoading}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      {t('manageSubscriptionBtn')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Company Information ── */}
          <div className="bg-card rounded-lg shadow-elevation-1 p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Building2" size={20} className="text-accent" />
              {t('companyInformation')}
            </h2>

            {isAdmin ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">{t('companyNameLabel')}</label>
                  <input
                    type="text"
                    value={editCompanyName}
                    onChange={e => setEditCompanyName(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder={t('companyNamePlaceholder')}
                    disabled={infoSaving}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">{t('contactEmailLabel')}</label>
                  <input
                    type="email"
                    value={editContactEmail}
                    onChange={e => setEditContactEmail(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder={t('contactEmailPlaceholder')}
                    disabled={infoSaving}
                  />
                </div>

                {infoError && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                    <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
                    {infoError}
                  </div>
                )}
                {infoSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                    <Icon name="CheckCircle2" size={16} className="flex-shrink-0" />
                    {t('companyDetailsSaved')}
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleSaveCompanyInfo}
                    loading={infoSaving}
                    disabled={infoSaving || !editCompanyName?.trim() || subscriptionReadOnly}
                    iconName={infoSaving ? undefined : 'Save'}
                    iconPosition="left"
                  >
                    {infoSaving ? t('saving') : t('saveChanges')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-center w-14 h-14 bg-accent/10 rounded-lg">
                  <Icon name="Building2" size={28} className="text-accent" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-lg">{userProfile?.company_name || 'Your Company'}</p>
                  <p className="text-sm text-muted-foreground">{t('company')}</p>
                </div>
              </div>
            )}
          </div>

          {/* ── Company Configuration — Admin only ── */}
          {isAdmin && (
            <div className="bg-card rounded-lg shadow-elevation-1 p-6 mb-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="SlidersHorizontal" size={20} className="text-accent" />
                  {t('siteComplianceSettings')}
                </h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                {t('siteComplianceSettingsDesc')}
              </p>

              {settingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent" />
                </div>
              ) : (
                <>
                  <div className="divide-y divide-border">
                    <ToggleRow
                      label={t('requirePhotoTaskLabel')}
                      description={t('requirePhotoTaskDesc')}
                      checked={settings?.require_photo_evidence}
                      onChange={(val) => handleToggle('require_photo_evidence', val)}
                      disabled={saving}
                    />
                    <ToggleRow
                      label={t('requirePhotoSnagLabel')}
                      description={t('requirePhotoSnagDesc')}
                      checked={settings?.require_photo_evidence_snag}
                      onChange={(val) => handleToggle('require_photo_evidence_snag', val)}
                      disabled={saving}
                    />
                    <ToggleRow
                      label={t('requireToolboxSignoffLabel')}
                      description={t('requireToolboxSignoffDesc')}
                      checked={settings?.require_toolbox_talk_signoff}
                      onChange={(val) => handleToggle('require_toolbox_talk_signoff', val)}
                      disabled={saving}
                    />
                    <ToggleRow
                      label={t('enableIncidentReportingLabel')}
                      description={t('enableIncidentReportingDesc')}
                      checked={settings?.enable_incident_reporting}
                      onChange={(val) => handleToggle('enable_incident_reporting', val)}
                      disabled={saving}
                    />
                  </div>

                  {/* ── Notifications ── */}
                  <div className="mt-6 pt-5 border-t border-border">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Bell" size={16} className="text-accent" />
                      Notifications
                    </h3>
                    <div className="space-y-1.5">
                      <label htmlFor="expiry_alert_days" className="block text-sm font-medium text-foreground">
                        Document expiry alert window
                      </label>
                      <input
                        id="expiry_alert_days"
                        type="number"
                        min={1}
                        max={365}
                        step={1}
                        value={settings?.expiry_alert_days ?? ''}
                        onChange={(e) => handleToggle('expiry_alert_days', e?.target?.value === '' ? '' : Number(e?.target?.value))}
                        disabled={saving || subscriptionReadOnly}
                        className={`w-32 px-3 py-2 rounded-lg border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 ${
                          expiryAlertDaysError ? 'border-destructive' : 'border-border focus:border-accent'
                        }`}
                        aria-invalid={!!expiryAlertDaysError}
                        aria-describedby="expiry_alert_days_help"
                      />
                      <p id="expiry_alert_days_help" className="text-xs text-muted-foreground leading-relaxed">
                        How many days before a document expires should the admin receive an alert? Between 1 and 365 days.
                      </p>
                      {expiryAlertDaysError && (
                        <p className="text-xs text-destructive mt-1" role="alert">{expiryAlertDaysError}</p>
                      )}
                    </div>
                  </div>

                  {saveError && (
                    <div className="mt-4 flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                      <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
                      {saveError}
                    </div>
                  )}

                  {saveSuccess && (
                    <div className="mt-4 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                      <Icon name="CheckCircle2" size={16} className="flex-shrink-0" />
                      {t('settingsSavedSuccess')}
                    </div>
                  )}

                  <div className="mt-5 flex justify-end">
                    <Button
                      variant="primary"
                      onClick={handleSaveSettings}
                      loading={saving}
                      disabled={saving || subscriptionReadOnly || !!expiryAlertDaysError}
                      iconName={saving ? undefined : 'Save'}
                      iconPosition="left"
                    >
                      {saving ? t('saving') : t('saveSettings')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {!isAdmin && (
            <div className="bg-card rounded-lg shadow-elevation-1 p-6 mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Settings" size={20} className="text-accent" />
                {t('companySettingsPageTitle')}
              </h2>
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border border-border">
                <Icon name="Info" size={18} className="text-muted-foreground flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {t('advancedSettingsAdminOnly')}
                </p>
              </div>
            </div>
          )}

          {/* ── Danger Zone ── */}
          <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-destructive/30">
            <h2 className="text-lg font-semibold text-destructive mb-2 flex items-center gap-2">
              <Icon name="AlertTriangle" size={20} className="text-destructive" />
              {t('dangerZone')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('deleteAccountDesc')}
            </p>

            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => { setShowDeleteConfirm(true); setDeleteError(''); }}
                iconName="Trash2"
                iconPosition="left"
              >
                {t('deleteAccountBtn')}
              </Button>
            ) : (
              <div className="space-y-4 p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-destructive">{t('deleteAccountPermanent')}</p>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-0.5 list-disc list-inside">
                      <li>{t('deleteAccountBullet1')}</li>
                      <li>{t('deleteAccountBullet2')}</li>
                      <li>{t('deleteAccountBullet3')}</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    {t('deleteAccountConfirmLabel')} <span className="font-mono font-bold text-destructive">{CONFIRM_PHRASE}</span> {t('deleteAccountConfirmLabel2')}
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e?.target?.value)}
                    placeholder={CONFIRM_PHRASE}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-destructive/40 focus:border-destructive"
                    disabled={deleting}
                  />
                </div>

                {deleteError && (
                  <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{deleteError}</p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setShowDeleteConfirm(false); setConfirmText(''); setDeleteError(''); }}
                    disabled={deleting}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    className="bg-destructive hover:bg-destructive/90 text-white"
                    onClick={handleDeleteAccount}
                    disabled={confirmText !== CONFIRM_PHRASE || deleting}
                    loading={deleting}
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    {deleting ? t('deletingAccount') : t('permanentlyDeleteAccount')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
